import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Comments from "../components/Comments";

const SEV = { high: "bg-red-600 text-white", medium: "bg-yellow-600 text-black", low: "bg-neutral-700 text-white" };

export default function CoachAthlete() {
  const { athleteId } = useParams();
  const [plan, setPlan] = useState(null);
  const [analyses, setAnalyses] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get(`/coach/athletes/${athleteId}/plan`),
      api.get(`/coach/athletes/${athleteId}/analyses`),
    ]).then(([p, a]) => { setPlan(p.data); setAnalyses(a.data.analyses || []); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Load failed"));
  }, [athleteId]);

  if (!plan) return <div className="text-neutral-400">Loading…</div>;

  return (
    <div>
      <Link to="/coach" className="text-neutral-400 hover:text-white text-sm font-display uppercase tracking-widest">← Back to roster</Link>
      <div className="rs-underline mt-4 mb-3" />
      <h1 className="font-display-heavy text-4xl">{plan.athlete_name.toUpperCase()}'S PLAN</h1>
      <p className="text-neutral-500 text-sm mt-1">Read-only view</p>

      <div className="mt-8">
        <h2 className="font-display-heavy text-2xl mb-4">CURRENT FOCUS</h2>
        {plan.active.length === 0 ? (
          <div className="border border-dashed border-neutral-800 rounded-md p-6 text-neutral-400 text-center">No active focus areas.</div>
        ) : (
          <div className="grid gap-3">
            {plan.active.map((it) => (
              <div key={it.id} data-testid={`coach-focus-${it.id}`} className="border border-neutral-800 rounded-md bg-neutral-900 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase text-neutral-400 font-display tracking-widest">{it.sport}</div>
                    <div className="font-display-heavy text-xl mt-1">{it.title}</div>
                    <p className="text-neutral-300 mt-2 text-sm">{it.latest_explanation}</p>
                    <div className="mt-3 text-sm">
                      <span className="text-neutral-500">Drills:</span>{" "}
                      {(it.drills || []).map((d, i) => (
                        <span key={d.id}>
                          <span className={d.completed ? "line-through text-neutral-500" : ""}>{d.name}</span>
                          {i < (it.drills || []).length - 1 && <span className="text-neutral-600"> · </span>}
                        </span>
                      ))}
                    </div>
                  </div>
                  <span className="text-red-500 font-display-heavy text-2xl">{it.occurrence_count}×</span>
                </div>
                <Comments focusItemId={it.id} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-10">
        <h2 className="font-display-heavy text-2xl mb-4">RECENT ANALYSES</h2>
        {analyses.length === 0 ? (
          <div className="border border-dashed border-neutral-800 rounded-md p-6 text-neutral-400 text-center">No analyses yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {analyses.slice(0, 8).map((a) => (
              <div key={a.id} className="border border-neutral-800 rounded-md bg-neutral-900 p-4">
                <div className="flex justify-between">
                  <div className="font-display-heavy">{a.sport}</div>
                  <div className="text-xs text-neutral-500">{new Date(a.created_at).toLocaleDateString()}</div>
                </div>
                {a.result?.summary && <div className="text-sm text-neutral-300 mt-2 line-clamp-3">{a.result.summary}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
